'use client'

import { Search, Filter, ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SortConfig, StatusFilter } from '../types/contentManagement'

const contentTypeLabels: Record<string, string> = {
  'ad_group': 'Ad Group',
  'blog_post': 'Blog Post',
  'landing_page': 'Landing Page',
  'email_sales_sequence': 'Sales Email Sequence',
  'email_educational_sequence': 'Educational Email Sequence',
  'email_newsletter': 'Email Newsletter',
  'quiz': 'Quiz',
  'video': 'Video',
  'tool': 'Tool',
  'social_post': 'Social Post'
};

export interface ContentFiltersProps {
  // Search state
  search: string;
  onSearchChange: (search: string) => void;

  // Content type filter state
  contentType: string;
  onContentTypeChange: (contentType: string) => void;

  // Status filter state
  statusFilter: StatusFilter;
  onStatusFilterChange: (statusFilter: StatusFilter) => void;

  // Platform filter state
  platformFilter: string;
  onPlatformFilterChange: (platformFilter: string) => void;

  // Sort configuration state
  sortConfig: SortConfig;
  onSortConfigChange: (sortConfig: SortConfig) => void;

  // Mobile filters state
  filtersExpanded: boolean;
  onFiltersExpandedChange: (expanded: boolean) => void;

  // View mode (affects which filters to show)
  viewMode: 'cards' | 'calendar';
}

export function ContentFilters({
  search,
  onSearchChange,
  contentType,
  onContentTypeChange,
  statusFilter,
  onStatusFilterChange,
  platformFilter,
  onPlatformFilterChange,
  sortConfig,
  onSortConfigChange,
  filtersExpanded,
  onFiltersExpandedChange,
  viewMode: _viewMode // Kept for future use and API consistency
}: ContentFiltersProps) {
  return (
    <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-purple-50/30" />
      <div className="relative">
        {/* Mobile: Collapsible filters with toggle button */}
        <div className="block lg:hidden">
          {/* Search bar - always visible on mobile */}
          <div className="p-4 border-b border-white/30">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search content..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10 h-10 bg-white/50 border-white/50 focus:bg-white/70 transition-colors text-sm"
              />
            </div>
          </div>
          
          {/* Filters toggle button */}
          <div className="p-4">
            <Button
              variant="ghost"
              onClick={() => onFiltersExpandedChange(!filtersExpanded)}
              className="w-full justify-between h-10 text-sm bg-white/30 hover:bg-white/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <span>Filters & Sorting</span>
                {(statusFilter !== 'all' || contentType !== 'all' || platformFilter !== 'all') && (
                  <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                )}
              </div>
              {filtersExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
          
          {/* Collapsible filters */}
          {filtersExpanded && (
            <div className="px-4 pb-4 space-y-3 border-t border-white/30 pt-4">
              {/* Status Filter */}
              <div>
                <label className="text-xs text-gray-600 font-medium mb-2 block">Status</label>
                <Select value={statusFilter} onValueChange={(value: StatusFilter) => onStatusFilterChange(value)}>
                  <SelectTrigger className="w-full h-10 bg-white/50 border-white/50 focus:bg-white/70 transition-colors text-sm">
                    <Filter className="h-3 w-3 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Content</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="held">Held</SelectItem>
                    <SelectItem value="scheduled_and_held">Scheduled & Held</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="scheduled_and_ready">Scheduled & Ready</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="generating">Generating</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="deleted">Deleted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Content Type Filter */}
              <div>
                <label className="text-xs text-gray-600 font-medium mb-2 block">Content Type</label>
                <Select value={contentType} onValueChange={onContentTypeChange}>
                  <SelectTrigger className="w-full h-10 bg-white/50 border-white/50 focus:bg-white/70 transition-colors text-sm">
                    <SelectValue placeholder="Content Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.entries(contentTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Platform Filter */}
              <div>
                <label className="text-xs text-gray-600 font-medium mb-2 block">Platform</label>
                <Select value={platformFilter} onValueChange={onPlatformFilterChange}>
                  <SelectTrigger className="w-full h-10 bg-white/50 border-white/50 focus:bg-white/70 transition-colors text-sm">
                    <SelectValue placeholder="Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Platforms</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="twitter">Twitter/X</SelectItem>
                    <SelectItem value="rss">RSS</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sort Dropdown */}
              <div>
                <label className="text-xs text-gray-600 font-medium mb-2 block">Sort By</label>
                <Select 
                  value={`${sortConfig.key}_${sortConfig.direction}`}
                  onValueChange={(value) => {
                    const lastUnderscoreIndex = value.lastIndexOf('_');
                    const key = value.substring(0, lastUnderscoreIndex);
                    const direction = value.substring(lastUnderscoreIndex + 1);
                    onSortConfigChange({ 
                      key: key as SortConfig['key'], 
                      direction: direction as 'asc' | 'desc' 
                    });
                  }}
                >
                  <SelectTrigger className="w-full h-10 bg-white/50 border-white/50 focus:bg-white/70 transition-colors text-sm">
                    <ArrowUpDown className="h-3 w-3 mr-2" />
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at_desc">Newest Created</SelectItem>
                    <SelectItem value="created_at_asc">Oldest Created</SelectItem>
                    <SelectItem value="published_at_desc">Recently Published</SelectItem>
                    <SelectItem value="published_at_asc">Oldest Published</SelectItem>
                    <SelectItem value="scheduled_time_desc">Recently Scheduled</SelectItem>
                    <SelectItem value="scheduled_time_asc">Oldest Scheduled</SelectItem>
                    <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                    <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
        
        {/* Desktop: Original layout */}
        <div className="hidden lg:block p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search content..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10 h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={(value: StatusFilter) => onStatusFilterChange(value)}>
                <SelectTrigger className="w-full sm:w-[160px] h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Content</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="held">Held</SelectItem>
                  <SelectItem value="scheduled_and_held">Scheduled & Held</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="scheduled_and_ready">Scheduled & Ready</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="generating">Generating</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  <SelectItem value="deleted">Deleted</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Content Type Filter */}
              <Select value={contentType} onValueChange={onContentTypeChange}>
                <SelectTrigger className="w-full sm:w-[180px] h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors">
                  <SelectValue placeholder="Content Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(contentTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Platform Filter */}
              <Select value={platformFilter} onValueChange={onPlatformFilterChange}>
                <SelectTrigger className="w-full sm:w-[160px] h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors">
                  <SelectValue placeholder="Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="twitter">Twitter/X</SelectItem>
                  <SelectItem value="rss">RSS</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort Dropdown */}
              <Select 
                value={`${sortConfig.key}_${sortConfig.direction}`}
                onValueChange={(value) => {
                  const lastUnderscoreIndex = value.lastIndexOf('_');
                  const key = value.substring(0, lastUnderscoreIndex);
                  const direction = value.substring(lastUnderscoreIndex + 1);
                  onSortConfigChange({ 
                    key: key as SortConfig['key'], 
                    direction: direction as 'asc' | 'desc' 
                  });
                }}
              >
                <SelectTrigger className="w-full sm:w-[180px] h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at_desc">Newest Created</SelectItem>
                  <SelectItem value="created_at_asc">Oldest Created</SelectItem>
                  <SelectItem value="published_at_desc">Recently Published</SelectItem>
                  <SelectItem value="published_at_asc">Oldest Published</SelectItem>
                  <SelectItem value="scheduled_time_desc">Recently Scheduled</SelectItem>
                  <SelectItem value="scheduled_time_asc">Oldest Scheduled</SelectItem>
                  <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                  <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}